import { useState } from "react";
import { Printer } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell } from "recharts";
import { useApi } from "@/pos/lib/api";
import { rm, todayStr, METHOD_LABEL } from "@/pos/lib/format";
import { Page, PageHeader, Stat, Empty } from "@/pos/components/Common";
import DateRange from "@/pos/components/DateRange";

const COLORS = ["#DC2626", "#3B82F6", "#D97706", "#E11D48", "#6366F1", "#2A0707", "#64748B"];

function Line({ label, value, bold, neg, testid }) {
  return (
    <div className={`flex justify-between py-2 ${bold ? "border-t border-slate-200 font-bold" : ""}`} data-testid={testid}>
      <span className={bold ? "" : "text-slate-600"}>{label}</span>
      <span className={`font-mono ${neg ? "text-rose-600" : ""}`}>{neg ? "(" : ""}{rm(value)}{neg ? ")" : ""}</span>
    </div>
  );
}

export default function Reports() {
  const [range, setRange] = useState([todayStr(-6), todayStr()]);
  const { data: r } = useApi("/reports/summary", { date_from: range[0], date_to: range[1] });
  return (
    <Page>
      <PageHeader title="Laporan Jualan & Untung Rugi" subtitle={`${range[0]} hingga ${range[1]}`}>
        <button className="btn-outline no-print" onClick={() => window.print()} data-testid="reports-print-btn"><Printer size={16} /> Cetak</button>
      </PageHeader>
      <div className="no-print mb-5"><DateRange value={range} onChange={setRange} /></div>
      {!r ? <div className="text-slate-500">Memuatkan...</div> : <>
        <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="reports-tab">
          <Stat label="Jualan" value={rm(r.revenue)} tone="sky" testid="report-revenue" hint={`${r.sales_count} transaksi`} />
          <Stat label="Untung Kasar" value={rm(r.gross_profit)} tone="green" testid="report-gross" hint={r.revenue ? `Margin ${((r.gross_profit / r.revenue) * 100).toFixed(1)}%` : ""} />
          <Stat label="Belanja + Susut" value={rm(r.expenses + r.losses)} tone="rose" testid="report-expenses" />
          <Stat label="Untung Bersih" value={rm(r.net_profit)} tone={r.net_profit < 0 ? "rose" : "green"} testid="report-net" />
        </div>
        <div className="grid gap-4 lg:grid-cols-5">
          <div className="card-surface p-5 lg:col-span-3">
            <h3 className="mb-4 font-heading text-lg font-bold">Jualan & Untung Harian</h3>
            <div className="h-64" data-testid="report-daily-chart">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={r.by_day}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} fontSize={12} />
                  <YAxis fontSize={12} width={50} />
                  <Tooltip formatter={(v) => rm(v)} />
                  <Area type="monotone" dataKey="jualan" name="Jualan" stroke="#DC2626" fill="#D1FADF" strokeWidth={2} />
                  <Area type="monotone" dataKey="untung" name="Untung" stroke="#3B82F6" fill="#DBEAFE" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="card-surface p-5 lg:col-span-2" data-testid="report-pnl">
            <h3 className="mb-2 font-heading text-lg font-bold">Penyata Untung Rugi</h3>
            <div className="text-sm">
              <Line label="Jualan bersih" value={r.revenue} />
              <Line label="Kos barang dijual" value={r.cogs} neg />
              <Line label="Untung kasar" value={r.gross_profit} bold />
              {r.expense_categories.map((e) => <Line key={e.name} label={e.name} value={e.value} neg />)}
              <Line label="Susut / rosak" value={r.losses} neg />
              <Line label="UNTUNG BERSIH" value={r.net_profit} bold testid="pnl-net" />
              <div className="mt-3 rounded-lg bg-slate-50 p-3 text-xs text-slate-500">Pembelian stok dalam tempoh ini: <b className="font-mono">{rm(r.purchases)}</b> (aliran tunai, bukan belanja).</div>
            </div>
          </div>
          <div className="card-surface p-5 lg:col-span-2">
            <h3 className="mb-2 font-heading text-lg font-bold">Ikut Kategori</h3>
            {r.by_category.length === 0 ? <Empty text="Tiada data" /> : <>
              <div className="h-48"><ResponsiveContainer width="100%" height="100%"><PieChart>
                <Pie data={r.by_category} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={2}>
                  {r.by_category.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie><Tooltip formatter={(v) => rm(v)} /></PieChart></ResponsiveContainer></div>
              {r.by_category.map((c, i) => (
                <div key={c.name} className="flex items-center justify-between py-1 text-sm"><span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />{c.name}</span><span className="font-mono">{rm(c.value)}</span></div>
              ))}
            </>}
            <div className="mt-4 border-t border-slate-100 pt-3">
              {Object.entries(r.by_payment).map(([k, v]) => <div key={k} className="flex justify-between py-1 text-sm"><span className="text-slate-600">{METHOD_LABEL[k]}</span><span className="font-mono">{rm(v)}</span></div>)}
            </div>
          </div>
          <div className="card-surface overflow-x-auto p-5 lg:col-span-3">
            <h3 className="mb-2 font-heading text-lg font-bold">Produk Terlaris</h3>
            <table className="data-table" data-testid="report-top-products">
              <thead><tr><th>Produk</th><th className="text-right">Kuantiti</th><th className="text-right">Jualan</th></tr></thead>
              <tbody>{r.top_products.map((p) => <tr key={p.name}><td className="font-medium">{p.name}</td><td className="text-right font-mono">{p.qty} {p.unit}</td><td className="text-right font-mono font-bold">{rm(p.total)}</td></tr>)}</tbody>
            </table>
            {r.top_products.length === 0 && <Empty text="Tiada jualan" />}
          </div>
        </div>
      </>}
    </Page>
  );
}
